import { createActionContext } from '../../interceptors/telemetry.interceptor';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable, forkJoin, mergeMap, of, Subject, Subscription, distinctUntilChanged, takeUntil } from 'rxjs';

import { ChatMessengerService } from '../chat-messenger.service';
import { CannedLibraryService, GetCannedLibraryMessageRequest, SmsCannedLibrary, SmsCannedLibraryMessage } from '../../../lib'
import { ToastService } from '../../toast/toast.service';

export interface LibraryResponse {
  library: SmsCannedLibrary;
  responses: SmsCannedLibraryMessage[];
}

export interface CannedResponse {
  libraryName: string;
  response: SmsCannedLibraryMessage;
}

@Component({
  selector: 'app-chat-responses',
  templateUrl: './chat-responses.component.html',
  styleUrls: ['./chat-responses.component.scss']
})
export class ChatResponsesComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private responsesSubscription?: Subscription;
  isLoading: boolean = true;
  libraryResponses: LibraryResponse[] = [];
  cannedResponses: CannedResponse[] = [];
  selectedLibraryIndex: number = -1;
  selectedResponseIndex: number = -1;
  syStudentId: number | undefined;
  searchString: string = '';

  constructor(
    private messenger: ChatMessengerService,
    private libraryService: CannedLibraryService,
    private toastService : ToastService
  ) { }

  ngOnInit(): void {
    this.messenger.$activeSyStudentId.pipe(distinctUntilChanged(), takeUntil(this.destroyed$)).subscribe(syStudentId => {
      this.syStudentId = syStudentId;
      this.getLibrariesAndResponses();
      this.backToLibrary()
    })
  }

  getLibrariesAndResponses(): void {
    this.responsesSubscription?.unsubscribe();
    this.libraryResponses = [];
    this.isLoading = true;
    const context = createActionContext('LoadCannedResponses');

    this.responsesSubscription = this.libraryService.apiSmsCannedLibraryPost({}, 'body', false, { context }).pipe(
      mergeMap(libraries => {
        let response$: Observable<SmsCannedLibraryMessage[]>[] = [];
        libraries.forEach(library => {
          if (library.id !== null && library.id !== undefined) {
            const req: GetCannedLibraryMessageRequest = {
              libraryId: library.id,
              syStudentId: this.syStudentId
            };

            response$.push(this.libraryService.apiSmsCannedLibraryMessagePost(req, 'body', false, { context }));
          }
        });

        return forkJoin([of(libraries), (response$.length ? forkJoin(response$) : of([]))]);
      })
    ).subscribe({ next: ([libraries, responses]) => {
      libraries.forEach((library, index) => {
        this.libraryResponses.push({ library: library, responses: responses[index] });
      });

      this.isLoading = false;
    }, error: () => {
      this.isLoading = false;
      this.toastService.show({ body: 'Unable to load canned responses. Please check your Genesys session and try again.', classname: 'bg-danger text-light', delay: 5000, header: 'SMS Canned Library Failed' });
    } });
  }

  ngOnDestroy(): void {
    this.responsesSubscription?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  librarySelected(index: number): void {
    this.selectedLibraryIndex = index;

    const libraryID = this.libraryResponses[index].library.id;
    if (libraryID) {
      this.cannedResponses = this.libraryResponses[index].responses.map(response => {
        const cannedResponse: CannedResponse = {
          libraryName: this.libraryResponses[index].library.name ?? '',
          response: response
        };
        return cannedResponse;
      });
    }
  }

  backToLibrary(): void {
    this.searchString = '';
    this.cannedResponses = [];
    this.selectedLibraryIndex = -1;
  }

  responseSelected(index: number): void {
    if (this.selectedResponseIndex === index) {
      this.selectedResponseIndex = -1;
    } else {
      this.selectedResponseIndex = index;
    }
  }

  applyResponse(index: number): void {
    const msg = this.cannedResponses?.[index].response.texts?.[0].content;
    if (msg) {
      this.messenger.setResponseText(msg);
    }
  }

  checkForEnterKey(event: KeyboardEvent): boolean {
    if (event.key === 'Enter') {
      if (this.searchString.length > 0) {
        this.search();
      }

      return false;
    } else {
      return true;
    }
  }

  search(): void {
    const lowerCaseSearchString = this.searchString.toLowerCase();
    this.cannedResponses = [];

    this.libraryResponses.forEach(libraryResponse => {
      const filteredResponses = libraryResponse.responses.filter(response =>
        response.name && response.name.toLowerCase().includes(lowerCaseSearchString)
      );

      if (filteredResponses.length > 0) {
        this.cannedResponses = this.cannedResponses.concat(filteredResponses.map(response => {
          const cannedResponse: CannedResponse = {
            libraryName: libraryResponse.library.name ?? '',
            response: response
          };
          return cannedResponse;
        }));
      }
    });

    this.selectedLibraryIndex = -2;
  }

}
